import { useMutation, useQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import React, { useState, useEffect } from 'react';
import {
  BASIC_BLOG_SETTINGS,
  BLOG_DEPLOYMENT_STATE,
} from '../../../helpers/graphql/hosting';
import BlogSettings from './BlogSettings';
import DeploymentStateIndicator from './DeploymentStateIndicator';
import DashboardCard from '../DashboardCard';
import usePreventRouteChangeIf from '../../../helpers/usePreventRouteChangeIf';
import Router from 'next/router';
import ConfirmDialog from '../../General/ConfirmDialog';
import Divider from '@mui/material/Divider';
import BuildMessage from './BuildMessage';

/* eslint-disable import/no-unused-modules */
export default function BasicBlogOptions({
  blog,
  setHasUnsavedChanges,
  hasUnsavedChanges,
  page,
  paymentSuccess,
  refetchHostingPlan,
}) {
  const [blogTitle, setBlogTitle] = useState(blog.blogTitle);
  const [blogSlogan, setBlogSlogan] = useState(blog.blogSlogan);
  const [logo, setLogo] = useState(blog.logo);
  const [logoDimensions, setLogoDimensions] = useState(
    blog.logoDimensions
      ? { width: blog.logoDimensions.width, height: blog.logoDimensions.height }
      : undefined,
  );
  const [defaultDarkMode, setDefaultDarkMode] = useState(blog.defaultDarkMode);
  const [darkLogo, setDarkLogo] = useState(blog.darkLogo);
  const [darkLogoDimensions, setDarkLogoDimensions] = useState(
    blog.darkLogoDimensions
      ? {
          width: blog.darkLogoDimensions.width,
          height: blog.darkLogoDimensions.height,
        }
      : undefined,
  );
  const [squareLogo, setSquareLogo] = useState(blog.squareLogo);
  const [primaryColor, setPrimaryColor] = useState({ hex: blog.primaryColor });
  const [defaultAddPosts, setDefaultAddPosts] = useState(blog.defaultAddPosts);
  const [redirectSubdomain, setRedirectSubdomain] = useState(
    blog.redirectSubdomain,
  );
  const [googleAnalytics, setGoogleAnalytics] = useState(
    blog.googleAnalyticsId,
  );
  const [headerInjection, setHeaderInjection] = useState(blog.headerInjection);
  const [adstxt, setAdstxt] = useState(blog.adstxt);
  const [status, setStatus] = useState();
  const [showValidation, setShowValidation] = useState(false);
  const [buildProgress, setBuildProgress] = useState(0);
  const [navigateTo, setNavigateTo] = useState(false);
  const [useHostingUrl, setUseHostingUrl] = useState(blog.useHostingUrl);

  const [basicBlogSettings] = useMutation(BASIC_BLOG_SETTINGS);

  const { enqueueSnackbar } = useSnackbar();

  usePreventRouteChangeIf(hasUnsavedChanges, url => {
    setNavigateTo(url);
  });

  const handleNavigate = async () => {
    setHasUnsavedChanges(false);
    setNavigateTo(false);
    await new Promise(r => setTimeout(r, 1));
    Router.push(navigateTo);
  };

  const handleInput = changeThis => event => {
    changeThis(event);
    setHasUnsavedChanges(true);
  };

  const increaseProgess = increase => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < 20; i++) {
      setTimeout(() => {
        setBuildProgress(oldProgress => oldProgress + increase / 20);
      }, 750 * i);
    }
  };

  const onCompleted = ({ blogDeploymentState }) => {
    if (blogDeploymentState === 'READY' || blogDeploymentState === 'ERROR') {
      // eslint-disable-next-line no-use-before-define
      if (status) setBuildProgress(100);
      else setBuildProgress(0);
    } else {
      setStatus('loading');
      if (buildProgress === 0) setBuildProgress(30);
      else if (buildProgress < 80) {
        increaseProgess(4);
      } else if (buildProgress < 90) {
        increaseProgess(2);
      } else if (buildProgress < 97) {
        increaseProgess(1);
      }
    }
  };

  // A v2 blog has no Pages build to watch — the deployment-state poll is
  // v1 machinery and would fire every 5s forever for nothing.
  const isV2Blog = blog.hostingV2Status === 'active';

  const { data, startPolling } = useQuery(BLOG_DEPLOYMENT_STATE, {
    variables: {
      id: blog.id,
    },
    onCompleted,
    notifyOnNetworkStatusChange: true,
    skip: isV2Blog,
  });

  useEffect(() => {
    if (!isV2Blog) startPolling(5000);
  }, [startPolling, isV2Blog]);

  // A v2 blog is online once its first publish lands: ask until it has
  // (READY) or gave up (PUBLISH_FAILED), then stop.
  const v2State = useQuery(BLOG_DEPLOYMENT_STATE, {
    variables: { id: blog.id },
    skip: !isV2Blog,
    fetchPolicy: 'network-only',
  });
  const v2DeploymentState = v2State.data?.blogDeploymentState;
  useEffect(() => {
    if (!isV2Blog) return;
    if (v2DeploymentState && v2DeploymentState !== 'BUILDING')
      v2State.stopPolling();
    else v2State.startPolling(10000);
  }, [isV2Blog, v2DeploymentState]);

  const handleEdit = () => {
    setBuildProgress(0);
    if (!blogTitle || !blogSlogan) {
      setShowValidation(true);
      enqueueSnackbar(
        `The ${!blogTitle ? 'blog title' : 'slogan'} cannot be empty`,
        {
          variant: 'error',
        },
      );
      return;
    }
    setHasUnsavedChanges(false);
    increaseProgess(5);
    setStatus('loading');
    basicBlogSettings({
      variables: {
        id: blog.id,
        settings: {
          blogTitle,
          blogSlogan,
          logo,
          logoDimensions,
          darkLogo,
          darkLogoDimensions,
          defaultDarkMode,
          squareLogo,
          primaryColor: primaryColor.hex,
          googleAnalyticsId: googleAnalytics,
          headerInjection,
          useHostingUrl,
          adstxt,
          defaultAddPosts,
          redirectSubdomain,
        },
      },
      // eslint-disable-next-line no-shadow
    }).then(({ data }) => {
      setStatus(data.basicBlogSettings);
      increaseProgess(5);
      startPolling(15000);
    });
  };

  // v2 blogs edit design and content in the site editor; the dashboard save
  // writes only what the dashboard still owns. Sending the full settings
  // snapshot here would replay stale hidden v1 fields over the blog doc.
  // useHostingUrl rides along unchanged so the mutation's change-detection
  // sweep stays quiet.
  // The toggle saves on change, so the fresh value arrives as an argument —
  // the state setter has not committed yet when this runs. The input type
  // requires title/slogan/color, so those ride along unchanged from mount
  // (nothing else writes them for a v2 blog); everything else stays out so
  // no stale hidden v1 field is replayed.
  const handleEditV2 = nextDefaultAddPosts => {
    setHasUnsavedChanges(false);
    basicBlogSettings({
      variables: {
        id: blog.id,
        settings: {
          blogTitle,
          blogSlogan,
          primaryColor: primaryColor.hex,
          defaultAddPosts:
            typeof nextDefaultAddPosts === 'boolean'
              ? nextDefaultAddPosts
              : defaultAddPosts,
          useHostingUrl,
        },
      },
      // eslint-disable-next-line no-shadow
    })
      .then(({ data }) => {
        const failed = data?.basicBlogSettings?.success === false;
        enqueueSnackbar(
          failed ? 'Could not save — please try again' : 'Saved',
          { variant: failed ? 'error' : 'success' },
        );
      })
      .catch(err => {
        console.error(err);
        enqueueSnackbar('Could not save — please try again', {
          variant: 'error',
        });
      });
  };

  const isBuilding = buildProgress > 0;

  return (
    <>
      <div className="pt-2">
        <DashboardCard
          titleSize="h5"
          shadowMd
          title={
            <div className="d-flex">
              <div>
                <DeploymentStateIndicator
                  state={
                    // For Starter plan, check hostingTrialExpiryDate for hosting status
                    // For other plans, check expiryDate
                    (blog.plan === 'starter' &&
                      blog.hostingTrialExpiryDate &&
                      new Date(blog.hostingTrialExpiryDate) < new Date()) ||
                    (blog.plan !== 'starter' &&
                      new Date(blog.expiryDate) < new Date())
                      ? 'Plan expired'
                      : isV2Blog
                        ? v2DeploymentState
                        : data && data.blogDeploymentState
                  }
                />
              </div>
              <div className="flex-grow-1">
                {blog.domain ? (
                  blog.domain.replace('https://', '')
                ) : (
                  <>
                    <strong>{blog.id}</strong>
                    <span className="d-none d-sm-inline tw-font-normal">
                      .truvvl.blog
                    </span>
                  </>
                )}
              </div>
            </div>
          }
          content={
            <>
              {isBuilding && (
                <>
                  {buildProgress < 100 && (
                    <Box display="flex" alignItems="center">
                      <Box width="100%" mr={1}>
                        <LinearProgress
                          variant="determinate"
                          value={buildProgress}
                        />
                      </Box>
                    </Box>
                  )}
                  <Typography variant="body1" className="pt-2">
                    <BuildMessage
                      buildProgress={buildProgress}
                      blogDeploymentState={
                        data ? data.blogDeploymentState : undefined
                      }
                    />
                  </Typography>
                  <div className="py-3">
                    <Divider />
                  </div>
                </>
              )}
              {isBuilding && buildProgress < 100 ? (
                <></>
              ) : (
                <div>
                  <BlogSettings
                    page={page}
                    paymentSuccess={paymentSuccess}
                    logo={logo}
                    setLogo={handleInput(setLogo)}
                    setLogoDimensions={handleInput(setLogoDimensions)}
                    darkLogo={darkLogo}
                    setDarkLogo={handleInput(setDarkLogo)}
                    setDarkLogoDimensions={handleInput(setDarkLogoDimensions)}
                    setDefaultDarkMode={handleInput(setDefaultDarkMode)}
                    defaultDarkMode={defaultDarkMode}
                    blogTitle={blogTitle}
                    setBlogTitle={handleInput(setBlogTitle)}
                    blogSlogan={blogSlogan}
                    setBlogSlogan={handleInput(setBlogSlogan)}
                    squareLogo={squareLogo}
                    setSquareLogo={handleInput(setSquareLogo)}
                    primaryColor={primaryColor}
                    setPrimaryColor={handleInput(setPrimaryColor)}
                    showValidation={showValidation}
                    defaultAddPosts={defaultAddPosts}
                    setDefaultAddPosts={handleInput(setDefaultAddPosts)}
                    redirectSubdomain={redirectSubdomain}
                    setRedirectSubdomain={handleInput(setRedirectSubdomain)}
                    googleAnalytics={googleAnalytics}
                    setGoogleAnalytics={handleInput(setGoogleAnalytics)}
                    headerInjection={headerInjection}
                    setHeaderInjection={handleInput(setHeaderInjection)}
                    useHostingUrl={useHostingUrl}
                    setUseHostingUrl={setUseHostingUrl}
                    adstxt={adstxt}
                    setAdstxt={handleInput(setAdstxt)}
                    isBuilding={isBuilding}
                    buildProgress={buildProgress}
                    blogId={blog.id}
                    editKey={blog.editKey}
                    blog={blog}
                    plan={blog.plan}
                    handleEdit={handleEdit}
                    handleEditV2={handleEditV2}
                    setBuildProgress={setBuildProgress}
                    setStatus={setStatus}
                    startPolling={startPolling}
                    refetchHostingPlan={refetchHostingPlan}
                  />
                </div>
              )}
            </>
          }
        />
      </div>
      {navigateTo && (
        <ConfirmDialog
          btnText="Discard Changes"
          dialogText={`You have unsaved changes that will be discarded if you leave this page. If you want to save your changes, press "cancel" and use the "Save changes" button on the bottom of the card.`}
          onConfirm={handleNavigate}
          open={navigateTo}
          setOpen={setNavigateTo}
        />
      )}
    </>
  );
}
